import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { BoxSelectionBoxSize, TextField, TextFieldTheme } from "@wix/wix-ui-tpa/cssVars";
import classNames from "classnames";
import { useMemo, useState, useCallback } from "react";
import { debounce } from "ts-debounce";
import { useConfig } from "../../contexts/config-context.js";
import { useForcedState, ForcedState } from "../../contexts/forced-state-context.js";
import { useTranslation } from "../../contexts/translation-context.js";
import { PRODUCT_PRICE_MULTIPLE_OF } from "../../field-settings/settings-shared/products/product-price.js";
import { useFieldAttributes } from "../../services/field-attributes.js";
import { mapById } from "../../utils/map-by-field.js";
import { FieldDescriptionWUT } from "../field-description/field-description-wut.js";
import { FieldErrorWUT } from "../field-error/field-error-wut.js";
import { FieldLabelWUT } from "../field-label/field-label-wut.js";
import { NUMBER_OF_COLUMNS } from "../options-layout/options-layout-constants.js";
import { getValidNumberInput } from "../shared/numeric-input-utils.js";
import { getCurrencyAriaLabel } from "../shared/payment-field-utils.js";
import { Widget } from "../widget/widget.js";
import { DonationFieldOption } from "./donation-field-option.js";
import { findProductWithDynamicPrice, getCustomAmount, getProductValueOnCheckboxChange, toProductValue } from "./donation-field-utils.js";
import { classes } from "./donation-field-wut.st.css";
import { Fieldset } from "../fieldset/fieldset.js";
import { OptionsLayout } from "../options-layout/options-layout.js";
const DonationFieldWUT = ({
  id,
  target,
  label,
  hideLabel = false,
  options,
  addOther,
  addOtherPlaceholder,
  required = false,
  value = null,
  onChange,
  onFocus,
  onBlur,
  error,
  hasError = false,
  errorMessage,
  disabled = false,
  description,
  validation,
  additionalInfo,
  FieldLayout,
  numberOfColumns = NUMBER_OF_COLUMNS.ONE,
  requiredIndicatorType,
  requiredIndicatorPlacement,
  widgetHtml,
  widgetEnabled
}) => {
  const {
    labelId,
    inputId,
    errorId,
    descriptionId,
    ariaDescribedBy,
    prefixId
  } = useFieldAttributes(id, error, description);
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const { t } = useTranslation();
  const { currency, regionalFormat } = useConfig();
  const productValue = value?.[0];
  const products = useMemo(
    () => validation?.predefined?.paymentOptions?.products || [],
    [validation]
  );
  const productsById = useMemo(() => mapById(products), [products]);
  const customAmountProduct = useMemo(
    () => addOther ? findProductWithDynamicPrice(products) : void 0,
    [products, addOther]
  );
  const [customAmount, setCustomAmount] = useState(
    getCustomAmount(productValue, customAmountProduct)
  );
  const handleChange = useCallback(
    (productId) => {
      onChange(
        getProductValueOnCheckboxChange(productId, productValue, productsById)
      );
      setCustomAmount("");
    },
    [onChange, productsById, setCustomAmount, productValue]
  );
  const handleCustomAmountChange = useCallback(
    (amount) => {
      if (!customAmountProduct) {
        return;
      }
      if (typeof amount === "number") {
        onChange([toProductValue(customAmountProduct, String(amount))]);
      } else {
        onChange([]);
      }
    },
    [onChange, customAmountProduct]
  );
  const debouncedHandleCustomAmountChange = useMemo(() => {
    return debounce(handleCustomAmountChange, 500);
  }, [handleCustomAmountChange]);
  const handleCustomAmountInputChange = useCallback(
    (event) => {
      if (productValue?.productId !== customAmountProduct?.id) {
        onChange(void 0);
      }
      const { validInput, validNumber } = getValidNumberInput(
        event.target.value,
        PRODUCT_PRICE_MULTIPLE_OF,
        customAmount,
        regionalFormat
      );
      setCustomAmount(validInput);
      debouncedHandleCustomAmountChange(validNumber);
    },
    [
      regionalFormat,
      onChange,
      productValue,
      customAmountProduct,
      customAmount,
      setCustomAmount,
      debouncedHandleCustomAmountChange
    ]
  );
  return /* @__PURE__ */ jsx(
    FieldLayout,
    {
      fieldId: id,
      renderLabel: () => /* @__PURE__ */ jsx(
        FieldLabelWUT,
        {
          labelId,
          required,
          label,
          hideLabel,
          requiredIndicatorType,
          requiredIndicatorPlacement
        }
      ),
      renderInput: () => widgetHtml && widgetEnabled ? /* @__PURE__ */ jsx(
        Widget,
        {
          fieldId: id,
          widgetHtml,
          onChange,
          value
        }
      ) : /* @__PURE__ */ jsxs(
        Fieldset,
        {
          id: inputId,
          ariaLabelledBy: labelId,
          className: classNames(classes.container),
          ariaDescribedBy,
          onFocus,
          onBlur,
          required,
          children: [
            options.length > 0 && /* @__PURE__ */ jsx(OptionsLayout, { numberOfColumns, tabIndex: -1, children: options.map(({ value: optionValue }, index) => {
              const isChecked = productValue?.productId === optionValue;
              const price = productsById[optionValue]?.fixedPriceOptions?.price;
              return /* @__PURE__ */ jsx(
                DonationFieldOption,
                {
                  id: optionValue,
                  name: target,
                  size: BoxSelectionBoxSize.small,
                  disabled,
                  "data-hook": `donation-${price}`,
                  checked: isChecked,
                  onChange: (event) => {
                    handleChange(event.id);
                  },
                  forcedHover,
                  price,
                  hasError,
                  inputId: index === 0 ? id : void 0
                },
                `donation-${optionValue}`
              );
            }) }),
            addOther && /* @__PURE__ */ jsx(
              TextField,
              {
                placeholder: addOtherPlaceholder === void 0 ? (
                  // only show to users who added donation field before placeholder defaultValue was added
                  t("donation.other-option.placeholder")
                ) : addOtherPlaceholder,
                "aria-labelledby": `${labelId} ${prefixId}`,
                className: classNames(classes.otherInput, classes.theme, {
                  [classes.hover]: forcedHover,
                  [classes.filled]: Boolean(customAmount)
                }),
                id: customAmountProduct?.id,
                value: customAmount,
                name: target,
                onChange: handleCustomAmountInputChange,
                disabled,
                min: 0,
                "data-hook": "donation-custom-amount",
                inputMode: "decimal",
                theme: TextFieldTheme.Box,
                error: hasError,
                newErrorMessage: true,
                prefix: customAmount && currency && /* @__PURE__ */ jsx(
                  "span",
                  {
                    id: prefixId,
                    "aria-hidden": false,
                    "aria-label": getCurrencyAriaLabel(t, currency),
                    children: currency.sign || currency.code
                  }
                ),
                autoComplete: "off"
              }
            )
          ]
        }
      ),
      renderDescription: () => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          FieldErrorWUT,
          {
            target,
            hasError,
            errorMessage,
            id: errorId
          }
        ),
        /* @__PURE__ */ jsx(FieldDescriptionWUT, { description, id: descriptionId }),
        additionalInfo
      ] })
    }
  );
};
export {
  DonationFieldWUT
};
//# sourceMappingURL=donation-field-wut.js.map
